import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Alert
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const DemoSessionFeedback = ({ open, onClose }) => {
  const { demoSession, logoutDemo } = useAuth();
  const [productFeedback, setProductFeedback] = useState('');
  const [contactRequested, setContactRequested] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const token = sessionStorage.getItem('demoToken');

      const response = await fetch('/api/demo/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          product_feedback: productFeedback || null,
          contact_requested: contactRequested,
          decline_reason: contactRequested === false && declineReason ? declineReason : null
        })
      });

      if (!response.ok) {
        console.warn('Feedback submission failed, but continuing with logout');
      }
    } catch (err) {
      console.warn('Feedback submission error:', err);
      // Don't block logout on feedback failure
    } finally {
      setSubmitting(false);
      // Always logout after feedback attempt
      logoutDemo();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          backgroundColor: 'rgb(212, 82, 33)',
          color: 'white',
          mx: -1,
          mt: -1,
          px: 3,
          pt: 2,
          borderRadius: '8px 8px 0 0'
        }}
      >
        <Typography
          variant="h4"
          component="div"
          sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontWeight: 'normal',
            color: 'white'
          }}
        >
          THANKS FOR TRYING SUPERVOIMA AI EVALUATOR!
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3, mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will end your demo session, no data has been stored.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please help us improve the product and give us feedback and ideas on how to improve.
          </Typography>

          {/* Product Feedback - Always visible */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              label="Help us improve AI Evaluator"
              placeholder="Share your feedback and ideas on how we can improve..."
              value={productFeedback}
              onChange={(e) => setProductFeedback(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem',
                  '&.Mui-focused fieldset': {
                    borderColor: '#021c2d'
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem'
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#021c2d'
                }
              }}
            />
          </Box>

          {/* Contact Preference */}
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 2 }}>
            Would you like us to contact you for more information about Supervoima?
          </Typography>

          <RadioGroup
            value={contactRequested === null ? '' : contactRequested.toString()}
            onChange={(e) => {
              setContactRequested(e.target.value === 'true');
              // Reset decline reason when switching back to "Yes"
              if (e.target.value === 'true') {
                setDeclineReason('');
              }
            }}
          >
            <FormControlLabel
              value="true"
              control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#021c2d' } }} />}
              label="Yes, please reach out"
              sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value="false"
              control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#021c2d' } }} />}
              label="Not right now"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </RadioGroup>

          {/* Sub-options when "Not right now" is selected */}
          {contactRequested === false && (
            <Box sx={{ mt: 2, ml: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Mind sharing why?
              </Typography>
              <RadioGroup
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
              >
                <FormControlLabel
                  value="Product did not meet expectations (feedback above)"
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#021c2d' } }} />}
                  label="Product did not meet expectations (feedback above)"
                  sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                />
                <FormControlLabel
                  value="I need internal approval first"
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#021c2d' } }} />}
                  label="I need internal approval first"
                  sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                />
                <FormControlLabel
                  value="Just exploring options"
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#021c2d' } }} />}
                  label="Just exploring options"
                  sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                />
                <FormControlLabel
                  value="Maybe later, timing isn't right"
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#021c2d' } }} />}
                  label="Maybe later, timing isn't right"
                  sx={{ mb: 0.5, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                />
                <FormControlLabel
                  value="Other"
                  control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#021c2d' } }} />}
                  label="Other"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                />
              </RadioGroup>
            </Box>
          )}

          {error && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{
            color: '#2f2f2f',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(47, 47, 47, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            backgroundColor: '#2f2f2f',
            color: 'white',
            textTransform: 'none',
            py: 1.5,
            px: 3,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: '#404040'
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(47, 47, 47, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)'
            }
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
              Submitting...
            </>
          ) : (
            'Submit & Exit'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemoSessionFeedback;
